/**
 * Adapters that convert response-side Proposal data into the shapes
 * PlanEditor's column builder and row builder expect.
 *
 * This is the bridge between ProposalCoverage/ProposalPlanGroup/ProposalPlan
 * (the "quote" side) and the existing RfpCoverage/BenefitPlanGroup/RfpBenefitPlan
 * grid types (the "request" side).
 *
 * The grid utilities (buildColumns, buildDataRows) operate on:
 *   • RfpCoverage  — flat list of RfpBenefitPlan[] for row data
 *   • BenefitPlanGroup[]  — groups with BenefitPlanSummary[] for column structure
 *
 * The adapters produce exactly these shapes from Proposal data so the
 * existing grid rendering code works unchanged.
 */

import {
  BenefitPlanTypeEnum as ApiBenefitPlanTypeEnum,
  PlanOptionNameEnum as ApiPlanOptionNameEnum,
  BaseBenefitPlanPolicyPeriod,
  CoverageTypeEnum as V5CoverageTypeEnum,
} from "@platform/openapi-types";
import {
  BenefitPlanTypeEnum,
  flattenRateGuaranteeIntoBenefits,
  LOC_MAP,
  PlanOptionNameEnum,
} from "@platform/plan-grid-core";
import { CoverageTypeEnum } from "@platform/types";

import { PlanRoleEnum } from "../types/proposal.types";

import { matchesProposalCoverageType } from "./coverageTypeNormalizer";

import type {
  BenefitPlanGroup,
  LocCoverageType,
  RfpBenefitPlan,
  RfpCoverage,
} from "@platform/plan-grid-core";
import type {
  Proposal,
  ProposalCoverage,
  ProposalPlan,
  ProposalPlanGroup,
} from "../types/proposal.types";

// Loose signature for the LOC-specific API→grid benefit transform.
type TransformFromApi = (benefits: unknown) => Record<string, unknown>;

/**
 * Label identifying a quoted plan's column, shared by the benefits grid and the
 * rates grid below it so the two always name a plan the same way.
 *
 * The RFP identifier (`DEN-1-2`) is deliberately not a fallback: it belongs to
 * the *requested* plan, so every carrier quoting that plan resolves to the same
 * string and the label stops identifying a column. The plan's own id is the only
 * value guaranteed to differ per column, and it is the key both grids join on.
 *
 * A synthesized placeholder/pad plan has no carrier response behind it and only a
 * synthetic id, so it reads "Not received" rather than an id that means nothing.
 */
export const quotedPlanLabel = (
  plan: Pick<ProposalPlan, "id" | "name" | "isPlaceholder">,
): string =>
  plan.name?.trim() ||
  (plan.isPlaceholder ? "Not received" : `ID: ${plan.id.slice(-8)}`);

// ---------------------------------------------------------------------------
// Unmapped Plans page (btx-unmapped-plans)
// ---------------------------------------------------------------------------

/**
 * Sentinel `selectedBpgId` value for the synthetic "Unmapped Plans" SBS page —
 * never a real BenefitPlanGroup id, so it can't collide with one.
 */
export const UNMAPPED_PAGE_ID = "__unmapped__";

/** True when the given page id is the synthetic "Unmapped Plans" page. */
export const isUnmappedPageId = (bpgId: string | undefined): boolean =>
  bpgId === UNMAPPED_PAGE_ID;

/**
 * True for a carrier's real, movable, unmapped proposal plan group — one the
 * broker or carrier's LOC ambiguity left without an `rfpBenefitPlanGroupId`
 * (btx-unmapped-plans). Excludes synthesized "awaiting quote" placeholder
 * groups (ADR-024), which are legitimately FK-less but have no backing
 * ProposalPlanGroup row and can't be moved.
 */
export const isUnmappedProposalGroup = (
  group: Pick<ProposalPlanGroup, "rfpBenefitPlanGroupId" | "isPlaceholder">,
): boolean => group.rfpBenefitPlanGroupId == null && !group.isPlaceholder;

/**
 * Reorders carrier column groups according to a specified carrier ID order. A carrier
 * may now own several column groups (its placeholder/quote column plus one per uploaded
 * document), so all of a carrier's groups move together, in their original insertion
 * order, the first time that carrier id appears in the order. Duplicate carrier ids in
 * the order (the sort util emits one entry per proposal) are ignored.
 */
const reorderCarrierGroupsByIds = (
  groups: CarrierColumnGroup[],
  carrierIdOrder: string[],
): CarrierColumnGroup[] => {
  const groupsByCarrier = new Map<string, CarrierColumnGroup[]>();
  for (const group of groups) {
    const list = groupsByCarrier.get(group.carrierId) ?? [];
    list.push(group);
    groupsByCarrier.set(group.carrierId, list);
  }

  const ordered: CarrierColumnGroup[] = [];
  const seen = new Set<string>();

  for (const carrierId of carrierIdOrder) {
    if (seen.has(carrierId)) continue;
    const carrierGroups = groupsByCarrier.get(carrierId);
    if (carrierGroups) {
      ordered.push(...carrierGroups);
      seen.add(carrierId);
    }
  }

  // Append any carriers not named in the order, preserving insertion order.
  for (const group of groups) {
    if (!seen.has(group.carrierId)) {
      ordered.push(group);
    }
  }

  return ordered;
};

// ---------------------------------------------------------------------------
// Plan role → policy period mapping
// ---------------------------------------------------------------------------

const PLAN_ROLE_TO_POLICY_PERIOD: Record<string, BaseBenefitPlanPolicyPeriod> =
  {
    [PlanRoleEnum.CURRENT]: BaseBenefitPlanPolicyPeriod.current,
    [PlanRoleEnum.QUOTED]: BaseBenefitPlanPolicyPeriod.alternate,
    [PlanRoleEnum.REQUESTED]: BaseBenefitPlanPolicyPeriod.newBusiness,
  };

// ---------------------------------------------------------------------------
// ProposalPlan → RfpBenefitPlan
// ---------------------------------------------------------------------------

/**
 * Converts a ProposalPlan to the RfpBenefitPlan shape that the grid's
 * row-data builder looks up by `plan.id` to extract benefit values.
 *
 * Fields the grid actually accesses:
 *   • id — join key between column defs and row data
 *   • benefits — the typed benefit object (shared union, already identical)
 *   • optionName — used for column headers when BPG has multiple plans
 *   • identifier — human-readable label; synthesised from name or id
 */
export const proposalPlanToRfpBenefitPlan = (
  plan: ProposalPlan,
  transformFromApi?: TransformFromApi,
): RfpBenefitPlan => ({
  id: plan.id,
  // Synthesise an identifier — the request side uses codes like "DEN-1-1"
  // but on the proposal side we use the plan name, or a positional label,
  // or fall back to the UUID only as a last resort.
  identifier:
    plan.name ?? (plan.position != null ? `Plan ${plan.position}` : plan.id),
  name: plan.name,
  type: plan.type as BenefitPlanTypeEnum | undefined,
  optionName:
    (plan.optionName as PlanOptionNameEnum) ??
    PlanOptionNameEnum.NOT_APPLICABLE,
  benefitClassAvailability: plan.benefitClassAvailability,
  isPlaceholder: plan.isPlaceholder,
  // Plan-level fields (siblings of benefits) the benefitPlanLevel grid cells read
  // and write. Without these the value round-trips to empty after each save.
  fundingType: plan.fundingType,
  participationRequirements: plan.participationRequirements,
  rateGuarantee: plan.rateGuarantee,
  rateCap: plan.rateCap,
  eligibilityClasses: plan.eligibilityClasses,
  // Apply LOC-specific transform (API nested → flat grid format) when available.
  // This mirrors the RFP side where skeletons/index.ts runs transformFromApi.
  // Rate Guarantee / Rate Caps is presented as a benefit-body field trio
  // (rateGuaranteeType/Amount/Cap) but persisted at plan level, so flatten the
  // plan's rateGuarantee/rateCap onto the benefits body for the grid to read.
  benefits: attachChildBenefitAmountsByAge(
    flattenRateGuaranteeIntoBenefits(
      plan.benefits && transformFromApi
        ? (transformFromApi(plan.benefits) as RfpBenefitPlan["benefits"])
        : plan.benefits,
      plan.rateGuarantee,
      plan.rateCap,
    ),
    plan.benefits,
  ),
});

/**
 * Surfaces the child age-band array onto the benefit body as the flat grid key
 * the Present child benefit-description editor reads and writes
 * (`childBenefitAmountsByAge`). transformFromApi flattens that array away into
 * the three fixed child fields, so the editor's full-array source is restored
 * here from the untransformed API benefits; the write path's toApi guard reads
 * the key back into `child.benefitAmountsByAge`. Voluntary Life only — other
 * coverages carry no `child.benefitAmountsByAge`, so this is a no-op for them.
 */
const attachChildBenefitAmountsByAge = (
  benefits: RfpBenefitPlan["benefits"],
  apiBenefits: ProposalPlan["benefits"],
): RfpBenefitPlan["benefits"] => {
  const child = (
    apiBenefits as
      | { child?: { benefitAmountsByAge?: unknown } }
      | null
      | undefined
  )?.child;

  const bands = child?.benefitAmountsByAge;

  if (!Array.isArray(bands)) {
    return benefits;
  }

  return {
    ...(benefits as Record<string, unknown>),
    childBenefitAmountsByAge: bands,
  } as RfpBenefitPlan["benefits"];
};

// ---------------------------------------------------------------------------
// ProposalPlanGroup → BenefitPlanGroup
// ---------------------------------------------------------------------------

/**
 * Converts a ProposalPlanGroup to the BenefitPlanGroup shape that
 * buildColumns uses to build the tiered column headers.
 *
 * Key mappings:
 *   • planRole → policyPeriod (for header display label)
 *   • plans → benefitPlans (as BenefitPlanSummary[])
 *   • productType stays the same
 *   • nickname is preferred for the column header
 */
export const proposalPlanGroupToBenefitPlanGroup = (
  group: ProposalPlanGroup,
): BenefitPlanGroup => ({
  id: group.id,
  createdAt: group.createdAt,
  updatedAt: group.updatedAt,
  position: group.position,
  productType:
    (group.productType as ApiBenefitPlanTypeEnum) ??
    ApiBenefitPlanTypeEnum.notApplicable,
  policyPeriod:
    PLAN_ROLE_TO_POLICY_PERIOD[group.planRole] ??
    BaseBenefitPlanPolicyPeriod.alternate,
  nickname: group.nickname,
  size: group.size,
  isPlaceholder: group.isPlaceholder,
  benefitPlans: group.plans.map((plan) => ({
    id: plan.id,
    identifier:
      plan.name ?? (plan.position != null ? `Plan ${plan.position}` : plan.id),
    optionName:
      (plan.optionName as ApiPlanOptionNameEnum) ??
      ApiPlanOptionNameEnum.notApplicable,
  })),
});

// ---------------------------------------------------------------------------
// ProposalCoverage → RfpCoverage + BenefitPlanGroup[]
// ---------------------------------------------------------------------------

/**
 * The result of adapting a ProposalCoverage for PlanEditor consumption.
 */
export interface ProposalGridData {
  /** Flat coverage structure with all plans (for buildDataRows). */
  coverage: RfpCoverage;
  /** Grouped plan structure (for buildColumns). */
  benefitPlanGroups: BenefitPlanGroup[];
}

const mergeWithBaseCoverage = (
  baseCoverage: RfpCoverage | undefined,
  proposalCoverage: ProposalCoverage,
  frontendCoverageType: CoverageTypeEnum,
  benefitPlans: RfpBenefitPlan[],
): RfpCoverage => {
  if (!baseCoverage) {
    return {
      id: proposalCoverage.id,
      rfpId: "",
      createdAt: proposalCoverage.createdAt,
      updatedAt: proposalCoverage.updatedAt,
      type: frontendCoverageType,
      coverageType:
        proposalCoverage.coverageType as unknown as V5CoverageTypeEnum,
      benefitPlans,
      benefitClasses: [],
    };
  }

  return {
    ...baseCoverage,
    id: proposalCoverage.id,
    createdAt: proposalCoverage.createdAt,
    updatedAt: proposalCoverage.updatedAt,
    type: frontendCoverageType,
    coverageType:
      proposalCoverage.coverageType as unknown as V5CoverageTypeEnum,
    benefitPlans,
  };
};

// ---------------------------------------------------------------------------
// Carrier-grouped data — multiple proposals into one unified grid
// ---------------------------------------------------------------------------

/**
 * A BenefitPlanGroup as it appears within a carrier-grouped column, extended
 * with MPX/Quotes-only metadata that has no place on the shared core type
 * (`plan-grid-core`'s BenefitPlanGroup serves Build too — see
 * `plan-grid-core/CLAUDE.md`'s core/MPX boundary).
 */
export interface RequestedBenefitPlanGroup extends BenefitPlanGroup {
  /**
   * The incumbent carrier's name for a synthetic "Incumbent/Requested" group
   * with policyPeriod === "current" — the first non-blank legacy free-text
   * `currentCarrierName` among the group's own benefit plans. Feeds
   * `formatRequestedColumnLabel`'s "<Carrier> - Current" column header.
   * Absent when the analyst never typed it (or when policyPeriod !==
   * "current", where it doesn't apply). Never set on a real carrier's own
   * plan group.
   */
  currentCarrierName?: string;
}

/**
 * One carrier's column data for the carrier-grouped grid.
 */
export interface CarrierColumnGroup {
  /** Carrier display name (used as the super-group header). */
  carrierName: string;
  /** Carrier ID from the proposal. */
  carrierId: string;
  /**
   * Stable, unique key for this column group. Carrier/analyst proposals share one
   * group per carrier (`carrierId`); each `origin = "document"` proposal is its own
   * group (`<carrierId>:document:<proposalId>`) so multiple uploaded documents render
   * as separate columns for one carrier (ADR-024). Used as the grid super-group id.
   * Leads with the real carrierId (matching the `<carrierId>:<bpgId>` shape used for
   * other additional lanes) so extractCarrierOrderFromEvent's `carrier-<id>` group-id
   * parsing recovers the actual carrier being dragged, not the literal word
   * "document" (CD-1806) — otherwise a document-origin carrier's column can't be
   * resequenced: the drag "succeeds" visually but the extracted id never matches a
   * real carrier, so the reorder silently drops back to its original position.
   */
  groupKey: string;
  /** Proposal-level status. */
  status: string;
  /**
   * Uploaded filename, set only for document-origin column groups. Shown in each quoted
   * plan's Source Document header cell so a broker can tell documents apart.
   */
  documentName?: string;
  /**
   * Version identity of the proposal that owns this lane (ADR-031). Each non-active
   * carrier/analyst version renders as its own column, so the grid uses these to show
   * the version chip, style the pending-requote column, and target the requote
   * actions menu (Replace/Disregard/Restore). Optional because synthetic columns (the
   * incumbent/requested baseline) have no backing proposal. Typed off Proposal so they
   * stay aligned with the model.
   */
  proposalId?: Proposal["id"];
  isActiveVersion?: Proposal["isActiveVersion"];
  requoteReviewState?: Proposal["requoteReviewState"];
  /**
   * The version number rendered in the chip: the carrier's Nth quote for the RFP-requested
   * plan this page is showing, read off the ProposalPlanGroup behind the column rather than
   * the coverage or the proposal. So two requested plans of one coverage number from 1
   * independently of each other and of the RFP's own version (CD-1767). Undefined for an
   * unquoted placeholder column and for an unmapped group, which has no requested plan to
   * number within.
   */
  quoteVersion?: ProposalPlanGroup["carrierQuoteVersion"];
  /** ISO timestamp the quote was received, for the version column's received label. */
  receivedAt?: Proposal["receivedAt"];
  /**
   * True when this lane's plan groups were deleted by the analyst (CD-89 AC1). Such a lane
   * only ever appears under the `all` read scope, in Quote History. Tracked separately from
   * `isActiveVersion` because deletion requires an active group, so the version fields still
   * describe it as live.
   */
  isDeleted?: boolean;
  /** BenefitPlanGroups belonging to this carrier (for column building). */
  benefitPlanGroups: RequestedBenefitPlanGroup[];
}

/**
 * Column-group key for a proposal: each uploaded document is its own column, keyed by the
 * stable documentId so a document's quote-history/re-versioned rows stay in one column group
 * (falling back to the proposal id when documentId is absent). A carrier's carrier/analyst
 * proposals (incl. quote-history versions) consolidate into one group by carrierId.
 *
 * The document lane is prefixed with the real carrierId (`<carrierId>:document:<docId>`)
 * rather than a bare `document:<docId>`, matching the `<carrierId>:<bpgId>` shape other
 * additional lanes already use — see the CD-1806 note on `CarrierColumnGroup.groupKey`.
 */
const carrierGroupKeyFor = (proposal: Proposal): string =>
  proposal.origin === "document"
    ? `${proposal.carrierId}:document:${proposal.documentId ?? proposal.id}`
    : proposal.carrierId;

/**
 * Routing keys for materializing a synthesized placeholder plan into a real
 * analyst proposal. Keyed by the placeholder plan id (which is carrier-scoped,
 * so it is unique across the merged grid).
 */
export interface PlaceholderMaterializeTarget {
  carrierId: string;
  /**
   * Carrier display name as shown on the placeholder column (resolved upstream;
   * falls back to the carrierId slug). Sent on materialize so the created
   * proposal persists a real name — BTX has no carrier directory to resolve it.
   */
  carrierName: string;
  rfpBenefitPlanGroupId: string;
  planPosition: number;
}

/**
 * The result of adapting multiple proposals for a single PlanEditor grid.
 */
export interface CarrierGroupedGridData {
  /** Merged flat coverage with all plans across all carriers (for buildDataRows). */
  coverage: RfpCoverage;
  /** Per-carrier column groups (for building carrier super-group headers). */
  carrierGroups: CarrierColumnGroup[];
  /**
   * Lookup from a placeholder plan id to the keys needed to materialize it.
   * Empty when no placeholder columns are present. The save path consults this
   * to route an edit on a placeholder cell to the materialize endpoint instead
   * of PATCHing a non-existent plan.
   */
  placeholderMaterializeByPlanId: Map<string, PlaceholderMaterializeTarget>;
  /**
   * Lookup from an empty pad plan id (a synthesized slot on a real under-quoted
   * group — CD-1612 partial fill) to the keys needed to fill it. The save path
   * consults this to route an edit on a pad cell to the add-plan-to-group
   * endpoint; the carrier header uses it to offer pad dismissal.
   */
  padFillByPlanId: Map<string, PadFillTarget>;
  /**
   * Ids of real (persisted, non-placeholder) plans that currently hold no
   * benefits — e.g. an unfilled slot of a multi-plan group the analyst
   * materialized (only the edited slot carries benefits) or a plan added via
   * "Add Plan". The save path treats the first benefit edit of such a plan like
   * a new column and strips the to-API transform's structural defaults so the
   * column isn't prepopulated (CD-1716).
   */
  emptyBenefitPlanIds: Set<string>;
}

/**
 * Routing keys for filling an empty pad slot on a real under-quoted group with
 * a real plan (CD-1612 partial fill). Keyed by the pad plan id
 * (`placeholder-<groupId>-<pos>`, unique across the merged grid and disjoint
 * from awaiting-quote placeholder plan ids, which embed carrier + RFP group).
 */
export interface PadFillTarget {
  proposalPlanGroupId: string;
  planPosition: number;
}

/**
 * Builds the pad plan id → add-plan-keys map for the given coverage type across
 * all proposals. Only synthesized pad plans on real (non-placeholder) groups
 * contribute — placeholder groups route through the materialize map instead.
 */
const buildPadFillMap = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum,
): Map<string, PadFillTarget> => {
  const map = new Map<string, PadFillTarget>();

  for (const proposal of proposals) {
    const proposalCoverage = proposal.coverages.find((c) =>
      matchesProposalCoverageType(c.coverageType, coverageType),
    );
    if (!proposalCoverage) continue;

    for (const group of proposalCoverage.planGroups) {
      if (group.isPlaceholder) continue;

      for (const plan of group.plans) {
        if (!plan.isPlaceholder || plan.position == null) continue;
        map.set(plan.id, {
          proposalPlanGroupId: group.id,
          planPosition: plan.position,
        });
      }
    }
  }

  return map;
};

/** True when a plan's persisted benefits carry no analyst-entered data. */
const isEmptyBenefit = (benefits: ProposalPlan["benefits"]): boolean => {
  if (!benefits) return true;
  return Object.keys(benefits).every((key) => key === "objectType");
};

/**
 * Builds the set of real (persisted, non-placeholder) plan ids whose benefits
 * are currently empty for the coverage type. Placeholder groups/plans are
 * excluded — their edits already route through materialize/pad-fill, which
 * strip defaults. The save path strips the first edit of a plan in this set so
 * a freshly materialized or added column isn't prepopulated (CD-1716).
 */
const buildEmptyBenefitRealPlanSet = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum,
): Set<string> => {
  const ids = new Set<string>();

  for (const proposal of proposals) {
    const proposalCoverage = proposal.coverages.find((c) =>
      matchesProposalCoverageType(c.coverageType, coverageType),
    );
    if (!proposalCoverage) continue;

    for (const group of proposalCoverage.planGroups) {
      if (group.isPlaceholder) continue;

      for (const plan of group.plans) {
        if (plan.isPlaceholder) continue;
        if (isEmptyBenefit(plan.benefits)) ids.add(plan.id);
      }
    }
  }

  return ids;
};

/**
 * Builds the placeholder plan id → materialize-keys map for the given coverage
 * type across all proposals. Only synthesized placeholder groups contribute.
 */
const buildPlaceholderMaterializeMap = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum,
): Map<string, PlaceholderMaterializeTarget> => {
  const map = new Map<string, PlaceholderMaterializeTarget>();

  for (const proposal of proposals) {
    const proposalCoverage = proposal.coverages.find((c) =>
      matchesProposalCoverageType(c.coverageType, coverageType),
    );
    if (!proposalCoverage) continue;

    for (const group of proposalCoverage.planGroups) {
      if (!group.isPlaceholder || !group.rfpBenefitPlanGroupId) continue;

      for (const plan of group.plans) {
        if (plan.position == null) continue;
        map.set(plan.id, {
          carrierId: proposal.carrierId,
          carrierName: proposal.carrierName,
          rfpBenefitPlanGroupId: group.rfpBenefitPlanGroupId,
          planPosition: plan.position,
        });
      }
    }
  }

  return map;
};

/**
 * Whether the proposal is the active version *of the coverage being rendered*, rather than of
 * its own proposal row. A plan-group-grain Replace promotes the requote's plan group and never
 * writes back to the proposal, so the row keeps reading inactive — ranking on it left the
 * newly active column sorted as history and shifted the board's carrier order on every
 * Replace (CD-1790). Falls back to the proposal for a coverage it does not quote.
 */
const isActiveForCoverage = (
  proposal: Proposal,
  coverageType: CoverageTypeEnum,
  groupFilter: (group: ProposalPlanGroup) => boolean,
): boolean => {
  const proposalCoverage = proposal.coverages.find((c) =>
    matchesProposalCoverageType(c.coverageType, coverageType),
  );

  if (!proposalCoverage) return proposal.isActiveVersion;

  return coverageActiveState(proposal, proposalCoverage, groupFilter)
    .isActiveVersion;
};

/**
 * Sorts a carrier's proposals into column order: the active version first, because it is the
 * quote in the marketing presentation, then the remaining versions newest to oldest (CD-89
 * AC10).
 *
 * Newest-first also satisfies CD-87 AC1 without a rule of its own: a freshly received requote
 * is the newest by definition, so it lands directly right of the active quote it is offered
 * against.
 */
const sortProposalsForQuoteHistory = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum,
  groupFilter: (group: ProposalPlanGroup) => boolean,
): Proposal[] =>
  [...proposals].sort((a, b) => {
    const aActive = isActiveForCoverage(a, coverageType, groupFilter);
    const bActive = isActiveForCoverage(b, coverageType, groupFilter);
    if (aActive !== bActive) return aActive ? -1 : 1;

    const aTime = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
    const bTime = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
    if (aTime !== bTime) return bTime - aTime;
    // receivedAt is missing on both (legacy-backfilled data) — fall back to rfpVersion
    // descending, the best available proxy for the order they arrived in.
    return b.rfpVersion - a.rfpVersion;
  });

/**
 * Orders proposals so that lane encounter order yields a stable carrier sequence:
 * carriers alphabetical by name with any current-plan carrier anchored first,
 * and within a carrier the quote-history order above.
 *
 * `carrierGroupOrder` is built from first encounter in this list, so the sequence
 * across carriers is decided here. Sorting the whole flat list by quote-history
 * order alone let any proposal created later — a materialized analyst column, a
 * requote — be encountered first and promote its carrier to the leftmost position,
 * reshuffling the board on save. Grouping by carrier also keeps each carrier's
 * lanes contiguous, which the column builder relies on.
 *
 * `orderProposalsForCarrierLanes` in centro-portal's carrier-sequence composer
 * computes this same order for the exported MPX spreadsheet — the export only
 * matches the tabs on screen while the two stay in sync.
 */
const sortProposalsForCarrierLanes = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum,
  groupFilter: (group: ProposalPlanGroup) => boolean,
): Proposal[] => {
  const byCarrier = new Map<string, Proposal[]>();

  for (const proposal of sortProposalsForQuoteHistory(
    proposals,
    coverageType,
    groupFilter,
  )) {
    const lane = byCarrier.get(proposal.carrierId);
    if (lane) lane.push(proposal);
    else byCarrier.set(proposal.carrierId, [proposal]);
  }

  const laneHasCurrentPlans = (lane: Proposal[]): boolean =>
    lane.some((proposal) =>
      proposal.coverages.some(
        (coverage) =>
          matchesProposalCoverageType(coverage.coverageType, coverageType) &&
          coverage.planGroups.some(
            (group) =>
              group.planRole === PlanRoleEnum.CURRENT || group.isCurrent,
          ),
      ),
    );

  return [...byCarrier.values()]
    .sort((a, b) => {
      const currentA = laneHasCurrentPlans(a) ? 0 : 1;
      const currentB = laneHasCurrentPlans(b) ? 0 : 1;
      if (currentA !== currentB) return currentA - currentB;

      return (a[0]?.carrierName ?? "").localeCompare(b[0]?.carrierName ?? "");
    })
    .flat();
};

// Dental is the only LOC that splits into separate HMO/PPO tabs (see
// coverageTypeNormalizer.ts) while proposals still carry a single "dental"
// coverage with both HMO and PPO plans mixed together. The main grid must
// only show the plans matching the active tab's product type.
export const DENTAL_LOC_PRODUCT_TYPE: Partial<
  Record<CoverageTypeEnum, string>
> = {
  [CoverageTypeEnum.DENTAL_PPO]: "ppo",
  [CoverageTypeEnum.DENTAL_HMO]: "hmo",
};

export const matchesLocProductType = (
  productType: string | undefined,
  coverageType: CoverageTypeEnum,
): boolean => {
  const expectedProductType = DENTAL_LOC_PRODUCT_TYPE[coverageType];
  if (!expectedProductType) return true;
  return productType?.toLowerCase() === expectedProductType;
};

/**
 * Derives the active/pending/historical state of the quote on screen from the plan groups
 * behind it (ADR-031, CD-1792 follow-up) rather than the whole proposal's — a
 * plan-group-scoped Replace/Disregard/Restore never updates
 * `Proposal.isActiveVersion`/`requoteReviewState`, so a proposal row keeps whatever it said
 * when the quote arrived. A plan group's own fields govern when present; an
 * unmapped/placeholder group (absent fields) falls back to the owning proposal's.
 *
 * `groupFilter` narrows this to the SBS page being drawn, and is not optional in spirit:
 * a carrier is active or archived *per requested plan*, and one coverage holds several.
 * Consulting the whole coverage let a sibling requested plan decide this page's column —
 * an archived quote inherited a sibling's active flag and rendered as the active one,
 * while a sibling's pending requote painted this page's active quote purple (CD-2133).
 *
 * It is the predicate that scopes groups to the page's requested plan, which is the grain
 * this state belongs to. Later stages can still drop groups from the rendered column for
 * unrelated reasons — the dental HMO/PPO split filters on product type, for one — so this
 * answers "what is this carrier's quote for this requested plan", not "what is in the
 * column after every other filter has run".
 *
 * With no group left after filtering, the proposal is the only thing to consult — a
 * coverage this carrier hasn't quoted on this page.
 *
 * Unlike the backend's `columnStateFromPlanGroups` (services/v1/sbs.go), this can't
 * assume every group is either active or pending: Quote History (`scope=all`) feeds
 * this adapter historical proposals too, so a page with no pending group and no
 * active group must resolve to historical (inactive, actioned) rather than being
 * promoted to active by default.
 */
const coverageActiveState = (
  proposal: Proposal,
  coverage: ProposalCoverage,
  groupFilter: (group: ProposalPlanGroup) => boolean,
): {
  isActiveVersion: boolean;
  requoteReviewState: Proposal["requoteReviewState"];
} => {
  const pageGroups = coverage.planGroups.filter(groupFilter);

  if (pageGroups.length === 0) {
    return {
      isActiveVersion: proposal.isActiveVersion,
      requoteReviewState: proposal.requoteReviewState,
    };
  }

  let anyActive = false;

  for (const group of pageGroups) {
    // A deleted group is quote history whatever its version fields say. Deleting requires
    // an active group, so it still reports isActiveVersion=true and would otherwise make
    // the page read as active — putting a deleted quote back on the board.
    if (group.deletedAt) continue;

    const isActive = group.isActiveVersion ?? proposal.isActiveVersion;
    const reviewState = group.requoteReviewState ?? proposal.requoteReviewState;

    if (reviewState === "pending") {
      return { isActiveVersion: false, requoteReviewState: "pending" };
    }

    if (isActive) anyActive = true;
  }

  return { isActiveVersion: anyActive, requoteReviewState: "actioned" };
};

/**
 * Adapts each proposal plan group into one BenefitPlanGroup, preserving the
 * backend's plan-group structure so multi-plan groups render as a
 * Carrier → Group → Plan hierarchy instead of one flat column per plan.
 */
const flattenProposalCoveragePlans = (
  proposalCoverage: Proposal["coverages"][number],
  coverageType: CoverageTypeEnum,
  groupFilter: (group: ProposalPlanGroup) => boolean,
  startPosition: number,
  isHistorical: boolean,
  isPendingRequote: boolean,
): { plans: RfpBenefitPlan[]; bpgs: BenefitPlanGroup[] } => {
  const locConfig = LOC_MAP[coverageType as LocCoverageType];
  const transformFromApi = locConfig?.transformFromApi as
    | TransformFromApi
    | undefined;

  const plans: RfpBenefitPlan[] = [];
  const bpgs: BenefitPlanGroup[] = [];
  let position = startPosition;

  for (const group of proposalCoverage.planGroups) {
    if (!groupFilter(group)) continue;

    // Dental splits one "dental" coverage into HMO/PPO tabs, so a group can
    // hold plans for both — keep only the ones matching the active LOC.
    const groupPlans = group.plans.filter((plan) =>
      matchesLocProductType(plan.type ?? group.productType, coverageType),
    );
    if (groupPlans.length === 0) continue;

    const benefitPlans = groupPlans.map((plan) => {
      plans.push(proposalPlanToRfpBenefitPlan(plan, transformFromApi));
      const planLabel = quotedPlanLabel(plan);
      return {
        id: plan.id,
        identifier: planLabel,
        optionName:
          (plan.optionName as ApiPlanOptionNameEnum) ??
          ApiPlanOptionNameEnum.notApplicable,
        position: plan.position,
      };
    });

    // An unmapped group never carries version state of its own — the columns are null —
    // so coverageActiveState falls back to its owning proposal's. That proposal's requote
    // lifecycle happened on a sibling requested plan: this group has none for a requote to
    // supersede. Letting it through would suffix the plan labels "(Previous)", paint the
    // column grey or purple, and hide Move Plan — the one action the Unmapped Plans page
    // exists to offer. buildCarrierHeaderParams already refuses the same state for the
    // carrier header's requote actions; this is that rule at the plan-group level.
    const isUnmapped = isUnmappedProposalGroup(group);

    bpgs.push({
      id: group.id,
      createdAt: group.createdAt,
      updatedAt: group.updatedAt,
      position: position++,
      productType:
        (group.productType as ApiBenefitPlanTypeEnum) ??
        (groupPlans[0].type as ApiBenefitPlanTypeEnum) ??
        ApiBenefitPlanTypeEnum.notApplicable,
      policyPeriod: undefined,
      nickname: group.nickname,
      size: benefitPlans.length,
      benefitPlans,
      isHistorical: isHistorical && !isUnmapped,
      isPendingRequote: isPendingRequote && !isUnmapped,
      isPlaceholder: group.isPlaceholder,
      isUnmapped,
      proposalPlanGroupId: group.id,
    });
  }

  return { plans, bpgs };
};

const buildActiveLaneOrderByCarrier = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum,
  groupFilter: (group: ProposalPlanGroup) => boolean,
): Map<string, string[]> => {
  const groupsByCarrier = new Map<
    string,
    { id: string; createdAt: string }[]
  >();

  for (const proposal of proposals) {
    if (proposal.origin === "document") continue;

    const proposalCoverage = proposal.coverages.find((c) =>
      matchesProposalCoverageType(c.coverageType, coverageType),
    );
    if (!proposalCoverage) continue;

    // Page-scoped, not proposal- or coverage-level — see coverageActiveState.
    if (
      !coverageActiveState(proposal, proposalCoverage, groupFilter)
        .isActiveVersion
    )
      continue;

    const groups = proposalCoverage.planGroups
      .filter(groupFilter)
      // A deleted group belongs to Quote History, so it must not compete for the base
      // carrier lane. This list is ordered by createdAt, so an older deleted group would
      // otherwise take that lane and render left of the live active quote.
      .filter((group) => !group.deletedAt)
      .filter((group) =>
        group.plans.some((plan) =>
          matchesLocProductType(plan.type ?? group.productType, coverageType),
        ),
      )
      .map((group) => ({ id: group.id, createdAt: String(group.createdAt) }));

    if (groups.length > 0) {
      // A carrier can have more than one active proposal for the same carrierId
      // (the active-version index is unique per origin, so origin "carrier" and
      // "analyst" can coexist), so accumulate their groups rather than
      // overwriting — otherwise one proposal's groups collapse into the base lane.
      const list = groupsByCarrier.get(proposal.carrierId) ?? [];
      list.push(...groups);
      groupsByCarrier.set(proposal.carrierId, list);
    }
  }

  const orderByCarrier = new Map<string, string[]>();
  for (const [carrierId, groups] of groupsByCarrier) {
    const orderedIds = [...groups]
      .sort(
        (a, b) =>
          a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
      )
      .map((group) => group.id);
    orderByCarrier.set(carrierId, [...new Set(orderedIds)]);
  }

  return orderByCarrier;
};

/**
 * Builds the per-group predicate that decides which carrier plan groups
 * appear on the selected page.
 *
 * - {@link UNMAPPED_PAGE_ID} selected: only real unmapped carrier groups
 *   (movable; excludes placeholders).
 * - A real page selected: strict FK match — a null-FK group never leaks
 *   onto a requested page, whether or not any group in the coverage has
 *   been linked yet.
 * - No page selected: every group.
 */
export const buildProposalGroupFilter = (
  rfpBenefitPlanGroupId: string | undefined,
): ((group: ProposalPlanGroup) => boolean) => {
  if (isUnmappedPageId(rfpBenefitPlanGroupId)) {
    return isUnmappedProposalGroup;
  }
  if (!rfpBenefitPlanGroupId) {
    return () => true;
  }
  return (group) => group.rfpBenefitPlanGroupId === rfpBenefitPlanGroupId;
};

/**
 * Merges multiple Proposals for a given coverage type into a
 * carrier-grouped grid structure for PropelX.
 *
 * Each proposal plan group becomes one BenefitPlanGroup, so a multi-plan
 * group renders as a Carrier → Group → Plan hierarchy and a single-plan
 * group collapses to a 1-tier Carrier → Plan column.
 *
 * When multiple proposals exist for the same carrier (quote history),
 * they are consolidated under a single carrier super-group column.
 * Active (most recent) plans appear first, followed by historical
 * versions ordered by `receivedAt` descending.
 *
 * Column hierarchy: Carrier → Benefit Plan
 *
 * @param rfpBenefitPlanGroupId - When provided, only plan groups linked to this RFP BPG are
 *   included — or, when it is {@link UNMAPPED_PAGE_ID}, only real unmapped carrier groups.
 * @param carrierIdOrder - When provided, carrier groups are sorted according to this order.
 */
export const proposalsToFlatCarrierGridData = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum,
  rfpBenefitPlanGroupId?: string,
  carrierIdOrder?: string[],
  baseCoverage?: RfpCoverage,
): CarrierGroupedGridData | null => {
  const allPlans: RfpBenefitPlan[] = [];

  // The sort needs the filter: it ranks the active quote first, and which quote is
  // active is a question about this page's requested plan (CD-2133).
  const groupFilter = buildProposalGroupFilter(rfpBenefitPlanGroupId);

  const sortedProposals = sortProposalsForCarrierLanes(
    proposals,
    coverageType,
    groupFilter,
  );

  // Group proposals into carrier lanes. Each document proposal is its own lane;
  // a carrier's carrier/analyst proposals (incl. quote-history versions)
  // consolidate by carrierId. A carrier whose active proposal has more than one
  // plan group renders one lane per plan group: the base group keeps the bare
  // carrierId lane, each additional group renders as its own
  // `${carrierId}:${planGroupId}` lane to its right — so a duplicated plan group
  // (all its plans) appears as its own column.
  const laneOrderByCarrier = buildActiveLaneOrderByCarrier(
    sortedProposals,
    coverageType,
    groupFilter,
  );

  // Lane key for a plan group of the active version: the base carrier lane, plus a
  // dedicated lane per additional plan group (e.g. a duplicated group). Non-active
  // versions do not use this — each gets its own version lane (see the loop below).
  const laneKeyFor = (
    carrierId: string,
    proposalPlanGroupId: string | undefined,
  ): string => {
    const ordered = laneOrderByCarrier.get(carrierId);
    if (!ordered || ordered.length <= 1) return carrierId;
    const index = proposalPlanGroupId
      ? ordered.indexOf(proposalPlanGroupId)
      : -1;

    if (!proposalPlanGroupId) return carrierId;
    if (index < 0) return `${carrierId}:${proposalPlanGroupId}`;

    return index > 0 ? `${carrierId}:${proposalPlanGroupId}` : carrierId;
  };

  /**
   * The lane a plan group of an active coverage belongs to. A deleted group gets one of
   * its own, so it reads as the Quote History column it is rather than merging into the
   * live quote's: it cannot use the base-lane path, which keys off the active lane order
   * that deliberately excludes deleted groups — and any group outside that order collapses
   * onto the base lane.
   */
  const activeCoverageLaneKey = (
    proposal: Proposal,
    coverage: ProposalCoverage,
    bpg: BenefitPlanGroup,
  ): string =>
    isDeletedBpg(coverage, bpg)
      ? `${proposal.carrierId}:deleted:${bpg.proposalPlanGroupId}`
      : laneKeyFor(proposal.carrierId, bpg.proposalPlanGroupId);

  const laneOrderIndex = (
    carrierId: string,
    proposalPlanGroupId: string | undefined,
  ): number => {
    const ordered = laneOrderByCarrier.get(carrierId);
    const index =
      ordered && proposalPlanGroupId
        ? ordered.indexOf(proposalPlanGroupId)
        : -1;
    return index < 0 ? (ordered?.length ?? 0) : index;
  };

  // The version is numbered per RFP-requested plan, so a lane takes it from the proposal
  // plan group behind the column rather than from the coverage — two requested plans of one
  // coverage render as separate SBS pages with independent sequences (CD-1767).
  const quoteVersionForBpg = (
    coverage: ProposalCoverage,
    bpg: BenefitPlanGroup,
  ): number | undefined =>
    coverage.planGroups.find((group) => group.id === bpg.proposalPlanGroupId)
      ?.carrierQuoteVersion;

  /** Whether the ProposalPlanGroup behind this column was deleted (CD-89 AC1). */
  const isDeletedBpg = (
    coverage: ProposalCoverage,
    bpg: BenefitPlanGroup,
  ): boolean =>
    !!coverage.planGroups.find((group) => group.id === bpg.proposalPlanGroupId)
      ?.deletedAt;

  const carrierGroupMap = new Map<string, CarrierColumnGroup>();
  const carrierGroupOrder: string[] = [];

  const routeBpgToLane = (
    laneKey: string,
    proposal: Proposal,
    coverage: ProposalCoverage,
    bpg: BenefitPlanGroup,
  ): void => {
    let lane = carrierGroupMap.get(laneKey);
    if (!lane) {
      const { isActiveVersion, requoteReviewState } = coverageActiveState(
        proposal,
        coverage,
        groupFilter,
      );
      lane = {
        carrierName: proposal.carrierName,
        carrierId: proposal.carrierId,
        groupKey: laneKey,
        status: proposal.status,
        documentName:
          proposal.origin === "document" ? proposal.documentName : undefined,
        proposalId: proposal.id,
        isActiveVersion,
        requoteReviewState,
        isDeleted: isDeletedBpg(coverage, bpg),
        quoteVersion: quoteVersionForBpg(coverage, bpg),
        receivedAt: proposal.receivedAt,
        benefitPlanGroups: [],
      };
      carrierGroupMap.set(laneKey, lane);
      carrierGroupOrder.push(laneKey);
    }
    bpg.position = lane.benefitPlanGroups.length;
    lane.benefitPlanGroups.push(bpg);
  };

  for (const proposal of sortedProposals) {
    const proposalCoverage = proposal.coverages.find((c) =>
      matchesProposalCoverageType(c.coverageType, coverageType),
    );

    if (!proposalCoverage || proposalCoverage.planGroups.length === 0) {
      continue;
    }

    // Derived from this coverage's own plan groups, not the proposal's — a
    // plan-group-scoped Replace/Disregard/Restore never updates the proposal's own
    // isActiveVersion/requoteReviewState, so a document proposal spanning several
    // coverage types can have some already replaced and others still pending
    // (CD-1792 follow-up). See coverageActiveState.
    const { isActiveVersion: pageIsActive, requoteReviewState } =
      coverageActiveState(proposal, proposalCoverage, groupFilter);
    // `!pageIsActive` alone would also be true for a pending requote — not just a
    // truly historical (superseded, actioned) version — which mislabels a pending
    // BPG's isHistorical, hiding Move Plan for an unmapped pending requote (its
    // movableGroup lookup excludes isHistorical groups). A version is historical
    // only once it's both inactive and actioned.
    const isPageHistorical = !pageIsActive && requoteReviewState === "actioned";

    const { plans, bpgs } = flattenProposalCoveragePlans(
      proposalCoverage,
      coverageType,
      groupFilter,
      0,
      isPageHistorical,
      requoteReviewState === "pending",
    );

    allPlans.push(...plans);

    // A non-active carrier/analyst/document version — a pending requote or an archived
    // historical predecessor — renders as its own column so the analyst sees every
    // version side by side (ADR-031). All of its plan groups route to one lane keyed
    // by the proposal id (unique per document too, so distinct documents' historical
    // versions never collide). sortProposalsForQuoteHistory has already placed these
    // after the active version, newest first, so columns read active → newest → oldest.
    if (!pageIsActive) {
      const laneKey = `${proposal.carrierId}:v:${proposal.id}`;
      for (const bpg of bpgs)
        routeBpgToLane(laneKey, proposal, proposalCoverage, bpg);
      continue;
    }

    // Documents keep their own lane for their active version — one column per
    // uploaded document (ADR-024).
    if (proposal.origin === "document") {
      const groupKey = carrierGroupKeyFor(proposal);
      for (const bpg of bpgs)
        routeBpgToLane(groupKey, proposal, proposalCoverage, bpg);
      continue;
    }

    // The active version keeps the base carrier lane; each additional plan group
    // (e.g. a duplicated group) routes to its own lane to the right. Sort by lane
    // order so the base lane is created before (left of) any additional lane.
    const orderedBpgs = [...bpgs].sort(
      (a, b) =>
        laneOrderIndex(proposal.carrierId, a.proposalPlanGroupId) -
        laneOrderIndex(proposal.carrierId, b.proposalPlanGroupId),
    );

    for (const bpg of orderedBpgs) {
      const laneKey = activeCoverageLaneKey(proposal, proposalCoverage, bpg);
      routeBpgToLane(laneKey, proposal, proposalCoverage, bpg);
    }
  }

  const carrierGroups = carrierGroupOrder
    .map((id) => carrierGroupMap.get(id)!)
    .filter((g) => g.benefitPlanGroups.length > 0);

  if (carrierGroups.length === 0) {
    return null;
  }

  // Apply custom carrier ordering when specified
  const orderedCarrierGroups =
    carrierIdOrder && carrierIdOrder.length > 0
      ? reorderCarrierGroupsByIds(carrierGroups, carrierIdOrder)
      : carrierGroups;

  // Build a merged coverage shell with all plans across all carriers
  const firstCoverage = sortedProposals
    .flatMap((p) => p.coverages)
    .find((c) => matchesProposalCoverageType(c.coverageType, coverageType))!;

  const coverage = mergeWithBaseCoverage(
    baseCoverage,
    firstCoverage,
    coverageType,
    allPlans,
  );

  return {
    coverage,
    carrierGroups: orderedCarrierGroups,
    placeholderMaterializeByPlanId: buildPlaceholderMaterializeMap(
      sortedProposals,
      coverageType,
    ),
    padFillByPlanId: buildPadFillMap(sortedProposals, coverageType),
    emptyBenefitPlanIds: buildEmptyBenefitRealPlanSet(
      sortedProposals,
      coverageType,
    ),
  };
};
